import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14567">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14568">
        <div className="flex justify-between items-center" data-id="element-14569">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14570">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14571" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-14572" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-14573">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-14574">
          <div data-id="element-14575">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14576">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-14577">
              <div className="flex items-start" data-id="element-14578">
                <div className="flex-shrink-0" data-id="element-14579">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-14580" />
                </div>
                <div className="ml-3" data-id="element-14581">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14582">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14583">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14584">
                <div className="flex-shrink-0" data-id="element-14585">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-14586" />
                </div>
                <div className="ml-3" data-id="element-14587">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14588">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14589">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14590">
                <div className="flex-shrink-0" data-id="element-14591">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-14592" />
                </div>
                <div className="ml-3" data-id="element-14593">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14594">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-14595">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14596">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14597">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-14598">
              <div className="flex items-start" data-id="element-14599">
                <div className="flex-shrink-0" data-id="element-14600">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-14601" />
                </div>
                <div className="ml-3" data-id="element-14602">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14603">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-14604">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14605">
                <div className="flex-shrink-0" data-id="element-14606">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-14607" />
                </div>
                <div className="ml-3" data-id="element-14608">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14609">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-14610">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-14611">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-14612">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14613">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-14614">
              <div className="flex items-start" data-id="element-14615">
                <div className="flex-shrink-0" data-id="element-14616">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-14617" />
                </div>
                <div className="ml-3" data-id="element-14618">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14619">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-14620">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-14621">
                <div className="flex-shrink-0" data-id="element-14622">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-14623" />
                </div>
                <div className="ml-3" data-id="element-14624">
                  <p className="text-sm font-medium text-gray-900" data-id="element-14625">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-14626">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}